import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAdminOverview } from "../../services/ingroove/adminService";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
} from "../../utils/formatters";
import StatCard from "../../components/common/StatCard";
import SectionPanel from "../../components/common/SectionPanel";
import Avatar from "../../components/common/Avatar";
import PageHeader from "../../components/common/PageHeader";
import {
  Users,
  Building2,
  Calendar,
  Ticket,
  TrendingUp,
  CircleDollarSign,
  AlertCircle,
} from "lucide-react";

const Overview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await getAdminOverview();
        setData(res);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading)
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <div
              key={i}
              className="bg-bg-surface border border-border rounded-xl h-24"
            />
          ))}
        </div>
      </div>
    );

  if (!data)
    return <p className="text-text-muted text-sm">Failed to load overview.</p>;

  const stats = [
    {
      icon: <Users size={16} className="text-primary" />,
      label: "Total Users",
      value: data.totalUsers?.toLocaleString(),
      accent: "bg-primary/10",
    },
    {
      icon: <Building2 size={16} className="text-blue-400" />,
      label: "Total Hubs",
      value: data.totalHubs?.toLocaleString(),
      accent: "bg-blue-400/10",
    },
    {
      icon: <Calendar size={16} className="text-purple-400" />,
      label: "Total Events",
      value: data.totalEvents?.toLocaleString(),
      accent: "bg-purple-400/10",
    },
    {
      icon: <Ticket size={16} className="text-green-400" />,
      label: "Tickets Sold",
      value: data.totalTicketsSold?.toLocaleString(),
      accent: "bg-green-400/10",
    },
    {
      icon: <TrendingUp size={16} className="text-teal-400" />,
      label: "Total Revenue",
      value: formatCurrency(data.totalRevenue),
      accent: "bg-teal-400/10",
    },
    {
      icon: <CircleDollarSign size={16} className="text-amber-400" />,
      label: "Fees Collected",
      value: formatCurrency(data.totalFeesCollected),
      accent: "bg-amber-400/10",
    },
    {
      icon: (
        <AlertCircle
          size={16}
          className={
            data.pendingPayouts > 0 ? "text-red-400" : "text-green-400"
          }
        />
      ),
      label: "Pending Payouts",
      value: data.pendingPayouts,
      sub: data.pendingPayouts > 0 ? "Requires action" : "All clear",
      accent: data.pendingPayouts > 0 ? "bg-red-400/10" : "bg-green-400/10",
    },
  ];

  return (
    <div className="space-y-8">
      <PageHeader
        title="Overview"
        subtitle="Platform-wide metrics across all hubs and events"
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending payouts */}
        <SectionPanel
          icon={<AlertCircle size={14} className="text-amber-400" />}
          title="Pending Payouts"
          onViewAll={() => navigate("/ingroove/payouts")}
          empty={!data.pendingPayoutsList?.length}
          emptyText="No pending payouts"
        >
          <div className="divide-y divide-border">
            {data.pendingPayoutsList?.map((payout) => (
              <div
                key={payout.id}
                className="flex items-center justify-between px-5 py-3 hover:bg-bg-elevated transition cursor-pointer"
                onClick={() => navigate("/ingroove/payouts")}
              >
                <div>
                  <p className="text-sm text-text-primary font-medium">
                    {payout.hubName}
                  </p>
                  <p className="text-xs text-text-muted mt-0.5">
                    {payout.eventTitle}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-amber-400">
                    {formatCurrency(payout.amount)}
                  </p>
                  <p className="text-xs text-text-muted mt-0.5">
                    {formatDate(payout.requestedAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </SectionPanel>

        {/* Recent signups */}
        <SectionPanel
          icon={<Users size={14} className="text-primary" />}
          title="Recent Signups"
          onViewAll={() => navigate("/ingroove/users")}
          empty={!data.recentUsers?.length}
          emptyText="No users yet"
        >
          <div className="divide-y divide-border">
            {data.recentUsers?.map((user) => (
              <div
                key={user.id}
                className="flex items-center gap-3 px-5 py-3 hover:bg-bg-elevated transition cursor-pointer"
                onClick={() => navigate("/ingroove/users")}
              >
                <Avatar
                  name={user.name}
                  surname={user.surname}
                  image={user.image}
                  size="sm"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-primary font-medium truncate">
                    {user.name} {user.surname}
                  </p>
                  <p className="text-xs text-text-muted truncate">
                    {user.email}
                  </p>
                </div>
                <p className="text-xs text-text-hint flex-shrink-0">
                  {formatDateTime(user.joinedAt)}
                </p>
              </div>
            ))}
          </div>
        </SectionPanel>
      </div>
    </div>
  );
};

export default Overview;
